import { FC, memo } from 'react';
import { cn } from '@new/lib';
import { isSameDay } from '@new/features/schedule/lib/helpers';

interface CalendarDayProps {
  date: Date | null;
  dayNumber: number | null;
  selectedDay: Date;
  today: Date;
  hasSlots: (date: Date | null) => boolean;
  onClick: (date: Date) => void;
  showAllDates?: boolean;
}

const CalendarDayComponent: FC<CalendarDayProps> = ({
  date,
  dayNumber,
  selectedDay,
  today,
  hasSlots,
  onClick,
  showAllDates = false,
}) => {
  const isSelected = date ? isSameDay(date, selectedDay) : false;
  const isToday = date ? isSameDay(date, today) : false;
  const hasSlotsForDay = hasSlots(date);
  const isDisabled = !date || (!showAllDates && !hasSlotsForDay);

  const baseClasses =
    'relative touch-manipulation w-9 h-9 sm:w-12 sm:h-12 rounded-full transition-all [font-size:var(--fs-l5)]! lg:[font-size:var(--fs-l3)]!';
  const emptyDayClasses = !date && 'invisible';
  const disabledDayClasses =
    date && !showAllDates && !hasSlotsForDay && 'text-(--neutral-black-NB400)! cursor-not-allowed';
  const availableDayClasses =
    date &&
    (showAllDates || hasSlotsForDay) &&
    !isSelected &&
    'text-(--neutral-black-NB1000)! hover:bg-(--neutral-black-NB100)! rounded-full! cursor-pointer font-semibold';
  const selectedDayClasses =
    isSelected && 'bg-(--primary-O200)! text-(--primary-O1000)! font-bold rounded-full!';
  const todayClasses = isToday && !isSelected && 'font-bold';

  return (
    <button
      type="button"
      onClick={() => date && onClick(date)}
      disabled={isDisabled}
      className={cn(
        baseClasses,
        emptyDayClasses,
        disabledDayClasses,
        availableDayClasses,
        selectedDayClasses,
        todayClasses
      )}
    >
      {dayNumber}
    </button>
  );
};

export const CalendarDay = memo(CalendarDayComponent);
